import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * AdminBudgetReconciliation — Quadratura Budget (sola lettura).
 * Per ogni squadra confronta (budget iniziale autorevole legacy_teams.balance + netto
 * di tutti i movimenti registrati) col budget attuale. Evidenzia gli scarti.
 * NON modifica nulla: serve solo a scovare incongruenze nei dati.
 */
function fmt(v) {
    if (v == null) return "—";
    const n = v / 1_000_000;
    if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(2)} mld`;
    return `${n.toFixed(2)} M`;
}

export default function AdminBudgetReconciliation() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);
    const [onlyDiff, setOnlyDiff] = useState(true);
    const [rectifying, setRectifying] = useState("");

    const load = async () => {
        setLoading(true); setErr("");
        try {
            const { data } = await api.get("/admin/budget-reconciliation");
            setData(data);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    };

    const rectify = async (row) => {
        const amt = (row.diff / 1_000_000).toFixed(2);
        if (!window.confirm(`Rettifica budget di ${row.name}?\n\nVerrà ${row.diff >= 0 ? "ACCREDITATO" : "ADDEBITATO"} ${Math.abs(amt)}M per allineare il budget a quello atteso (${fmt(row.expected_budget)}).\n\nNON crea un nuovo movimento nel ledger. Procedere?`)) return;
        setRectifying(row.team_id); setErr("");
        try {
            await api.post(`/admin/teams/${row.team_id}/rectify-budget`);
            await load();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setRectifying("");
        }
    };

    useEffect(() => { load(); }, []);

    const rows = (data?.rows || []).filter((r) => (onlyDiff ? (r.diff != null && r.diff !== 0) : true));

    return (
        <div data-testid="admin-budget-reconciliation-page">
            <div className="op-label" style={{ color: "rgb(var(--tc-e8ff00))" }}>// QUADRATURA BUDGET</div>
            <h2 className="op-heading text-3xl mt-2 mb-2">Quadratura budget squadre</h2>
            <p className="op-mono text-xs text-t8f95a3 mb-5 max-w-3xl leading-relaxed">
                Per ogni squadra confronto <strong className="text-tffffff">budget iniziale</strong> (da <code>legacy_teams</code>)
                {" "}+ <strong className="text-tffffff">netto movimenti registrati</strong> con il <strong className="text-tffffff">budget attuale</strong>.
                Se c'è uno <span className="text-tffb020">scarto</span>, significa che un movimento è registrato ma il
                denaro non è stato accreditato/addebitato (o viceversa). <strong className="text-tffffff">Sola lettura: non modifica nulla.</strong>
            </p>

            <div className="flex items-center gap-3 mb-4 flex-wrap">
                <button
                    onClick={load}
                    disabled={loading}
                    className="op-mono text-xs uppercase tracking-wider px-4 py-2 border border-te8ff00 text-te8ff00 hover:bg-te8ff00/10 transition-colors disabled:opacity-40"
                    data-testid="recon-refresh"
                >
                    {loading ? "Analisi…" : "↻ Ricalcola"}
                </button>
                <label className="op-mono text-xs text-t8f95a3 flex items-center gap-2 cursor-pointer" data-testid="recon-onlydiff-label">
                    <input type="checkbox" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} data-testid="recon-onlydiff" />
                    Mostra solo scarti
                </label>
                {data && (
                    <span className="op-mono text-xs" data-testid="recon-summary">
                        <span className="text-t8f95a3">{data.teams_count} squadre · </span>
                        <span className={data.discrepancies_count === 0 ? "text-t2bd96b" : "text-tffb020"}>
                            {data.discrepancies_count === 0 ? "✓ tutto quadra" : `${data.discrepancies_count} scarti (tot ${fmt(data.total_diff)})`}
                        </span>
                    </span>
                )}
            </div>

            {err && (
                <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-xs p-2 mb-4" data-testid="recon-err">{err}</div>
            )}

            <div className="op-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-t8f95a3 border-b border-t22252a">
                        <tr>
                            <th className="py-2 px-3 text-left">Squadra</th>
                            <th className="py-2 px-3 text-right">Budget iniziale</th>
                            <th className="py-2 px-3 text-right">Netto movimenti</th>
                            <th className="py-2 px-3 text-right">Budget atteso</th>
                            <th className="py-2 px-3 text-right">Budget attuale</th>
                            <th className="py-2 px-3 text-right">Scarto</th>
                            <th className="py-2 px-3 text-right">Azione</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const hasDiff = r.diff != null && r.diff !== 0;
                            return (
                                <tr key={r.team_id} className="border-b border-t1a1d24 hover:bg-t0f1115" data-testid={`recon-row-${r.team_id}`}>
                                    <td className="py-2 px-3 text-tffffff">{r.name}</td>
                                    <td className="py-2 px-3 text-right op-mono text-te8ff00">{r.legacy_balance == null ? "no legacy" : fmt(r.legacy_balance)}</td>
                                    <td className="py-2 px-3 text-right op-mono" style={{ color: r.net >= 0 ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-ff3b30))" }}>{r.net >= 0 ? "+" : "−"}{fmt(Math.abs(r.net))}</td>
                                    <td className="py-2 px-3 text-right op-mono text-t8f95a3">{fmt(r.expected_budget)}</td>
                                    <td className="py-2 px-3 text-right op-mono text-tffffff">{fmt(r.current_budget)}</td>
                                    <td className="py-2 px-3 text-right op-mono font-bold" style={{ color: !hasDiff ? "rgb(var(--tc-2bd96b))" : "rgb(var(--tc-ffb020))" }} data-testid={`recon-diff-${r.team_id}`}>
                                        {r.diff == null ? "—" : (r.diff === 0 ? "✓ 0" : `${r.diff > 0 ? "+" : "−"}${fmt(Math.abs(r.diff))}`)}
                                    </td>
                                    <td className="py-2 px-3 text-right">
                                        {hasDiff ? (
                                            <button
                                                onClick={() => rectify(r)}
                                                disabled={rectifying === r.team_id}
                                                className="op-mono text-[10px] uppercase tracking-wider px-3 py-1 border border-t7ce38b text-t7ce38b hover:bg-t7ce38b hover:text-black transition-colors disabled:opacity-40"
                                                data-testid={`recon-rectify-${r.team_id}`}
                                            >
                                                {rectifying === r.team_id ? "..." : "Rettifica"}
                                            </button>
                                        ) : (
                                            <span className="op-mono text-[10px] text-t5c6370">—</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {rows.length === 0 && !loading && (
                            <tr><td colSpan={7} className="py-6 text-center op-mono text-xs text-t2bd96b" data-testid="recon-empty">✓ Nessuno scarto: tutti i budget quadrano.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {data?.teams_without_legacy?.length > 0 && (
                <div className="op-mono text-[11px] text-t8f95a3 mt-3" data-testid="recon-no-legacy">
                    Senza budget iniziale legacy ({data.teams_without_legacy.length}): {data.teams_without_legacy.join(", ")}
                </div>
            )}
        </div>
    );
}
